import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { CheckCircle, Star, Loader, Clock, AlertCircle, Shield } from "lucide-react";
import Tooltip from "../../components/Tooltip/JobSeekerTooltip";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import gamificationService from "../../services/gamificationService";
import toast from "react-hot-toast";
import { useProctoring } from "../../hooks/useProctoring";
import ProctoringSetupModal from "../../components/Proctoring/ProctoringSetupModal";
import ProctoringBanner from "../../components/Proctoring/ProctoringBanner";
import { getPendingTest, clearPendingTest } from "../../utils/pendingTestStorage";

const SkillsValidation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Initializing...");
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [profileData, setProfileData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30); // 30 seconds per question
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [preventedActions, setPreventedActions] = useState(0);
  const [showProctoringSetup, setShowProctoringSetup] = useState(false);
  const [proctoringInitialized, setProctoringInitialized] = useState(false);

  // Log proctoring violations to Firebase
  const logViolationToFirebase = async (violation) => {
    if (!user) return;
    try {
      const resultId = `${user.id}_skills_validation`;
      const resultDoc = doc(db, "skillsValidationResults", resultId);
      const resultSnapshot = await getDoc(resultDoc);

      let existingViolations = [];
      if (resultSnapshot.exists()) {
        existingViolations = resultSnapshot.data().proctoringViolations || [];
      }

      await setDoc(
        resultDoc,
        {
          proctoringViolations: [
            ...existingViolations,
            {
              type: violation.type,
              timestamp: new Date().toISOString(),
              details: violation.details || {}
            }
          ]
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Failed to log violation:", error);
    }
  };

  // Memoize the callback to prevent infinite loops/re-renders in useProctoring
  const handleViolation = React.useCallback((violation) => {
    logViolationToFirebase(violation);
    setPreventedActions(prev => prev + 1);
  }, [user, db]);

  // Initialize proctoring with the hook
  const { proctoringStatus, startProctoring, stopProctoring, clearViolations } =
    useProctoring({
      enableTabSwitchDetection: true,
      enableCopyPasteDetection: true,
      enableFullscreenEnforcement: true,
      enableRightClickDisable: true,
      enableKeyboardShortcuts: true,
      enableCameraMonitoring: false, // No camera for skills validation
      enableMicrophoneMonitoring: false,
      maxViolations: 5,
      onViolation: handleViolation,
      onMaxViolationsReached: () => {
        toast.error("Too many proctoring violations detected. The test will be terminated.");
        handleTestSubmit();
      },
    });

  useEffect(() => {
    const generateTest = async () => {
      if (!user) return;

      try {
        setLoadingMessage("Loading your profile data...");

        // Get user profile data
        const profileDoc = await getDoc(doc(db, "userProfiles", user.id));

        if (!profileDoc.exists() || !profileDoc.data().resumeData) {
          alert("Please upload your resume first to validate your skills.");
          navigate("/job-seeker/profile");
          return;
        }

        const profileData = profileDoc.data();
        setProfileData(profileData);

        setLoadingMessage("Analyzing your skills and experience...");
        await new Promise(resolve => setTimeout(resolve, 1500));

        setLoadingMessage("Generating personalized test questions...");
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Generate questions based on resume data
        const questions = await generateSkillsQuestions(profileData.resumeData);

        setLoadingMessage("Finalizing your test...");
        await new Promise(resolve => setTimeout(resolve, 1000));

        setQuestions(questions);
        setLoading(false);
      } catch (error) {
        console.error("Failed to generate skills validation test:", error);
        alert("Failed to generate skills validation test. Please try again.");
        navigate("/job-seeker/profile");
      }
    };

    generateTest();
  }, [user, db, navigate]);

  // Timer effect (Per Question)
  useEffect(() => {
    if (testStarted && !testCompleted && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleNextQuestion(); // Auto-advance when time runs out
            return 30; // Reset for next (though handleNextQuestion handles logic)
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [testStarted, testCompleted, timeRemaining, currentQuestionIndex, handleNextQuestion]);

  // Proctoring is now handled by the useProctoring hook
  // Update fullscreen state from proctoringStatus
  useEffect(() => {
    setIsFullscreen(proctoringStatus.isFullscreen);
  }, [proctoringStatus.isFullscreen]);

  // Clean up proctoring when test completes
  useEffect(() => {
    if (testCompleted && proctoringStatus.isActive) {
      stopProctoring();
    }
  }, [testCompleted, proctoringStatus.isActive, stopProctoring]);

  const generateSkillsQuestions = async (resumeData) => {
    const skills = resumeData.professionalInfo?.skills || resumeData.skills || [];

    // Get Firebase ID token for authentication
    const auth = getAuth(firebaseApp);
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const idToken = await currentUser.getIdToken();

    // Call the generateSkillsTest HTTP endpoint (same  uses)
    const response = await fetch(
      "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateSkillsTest",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          skills: Array.isArray(skills) ? skills.slice(0, 10) : [],
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API request failed with status ${response.status}: ${errorData.error || ''}`);
    }

    const result = await response.json();
    let rawQuestions = result.questions || [];
    console.log(`[API] Got ${rawQuestions.length} questions`);

    // If we got less than 20 questions, make additional calls to get more
    let attempts = 0;
    while (rawQuestions.length < 20 && attempts < 3) {
      attempts++;
      console.log(`[API] Need more questions, making attempt ${attempts}...`);

      const additionalResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateSkillsTest",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            skills: Array.isArray(skills) ? skills.slice(0, 10) : [],
          }),
        }
      );

      if (additionalResponse.ok) {
        const additionalResult = await additionalResponse.json();
        const additionalQuestions = additionalResult.questions || [];
        rawQuestions = [...rawQuestions, ...additionalQuestions];
        console.log(`[API] Now have ${rawQuestions.length} questions total`);
      }
    }

    // Validate we have at least 10 questions minimum
    if (!Array.isArray(rawQuestions) || rawQuestions.length < 10) {
      const count = Array.isArray(rawQuestions) ? rawQuestions.length : 0;
      throw new Error(`Failed to generate enough questions. Got ${count}, need minimum 10`);
    }

    // Use exactly 20 questions (or pad with repeated questions if needed)
    if (rawQuestions.length < 20) {
      console.log(`[API] Warning: Only got ${rawQuestions.length} questions, padding to 20`);
      while (rawQuestions.length < 20) {
        rawQuestions.push(rawQuestions[rawQuestions.length % Math.min(10, rawQuestions.length)]);
      }
    }

    return rawQuestions.slice(0, 20);
  };

  const handleStartTest = () => {
    // Show proctoring setup modal before starting the test
    setShowProctoringSetup(true);
  };

  const handleProctoringStarted = async () => {
    try {
      // Start proctoring
      await startProctoring();
      setProctoringInitialized(true);
      setShowProctoringSetup(false);
      setTestStarted(true);

      // Log proctoring start
      if (user) {
        const resultId = `${user.id}_skills_validation`;
        await setDoc(
          doc(db, "skillsValidationResults", resultId),
          {
            proctoringStarted: true,
            proctoringStartedAt: serverTimestamp(),
            testStartedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
    } catch (error) {
      console.error("Failed to start proctoring:", error);
      toast.error("Warning: Proctoring system could not be fully initialized.");

      // Still allow the test to start, but flag it
      setProctoringInitialized(true);
      setShowProctoringSetup(false);
      setTestStarted(true);
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: answer,
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleTestSubmit();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleTestSubmit = async () => {
    if (testCompleted) return;

    setTestCompleted(true);
    setLoadingMessage("Analyzing your test...");
    setLoading(true);

    try {
      // Calculate score and identify mistakes
      let correctAnswers = 0;
      const userAnswers = [];
      const correctAnswerIndices = [];

      questions.forEach((question, index) => {
        const userAnswer = selectedAnswers[index];
        const correctAnswer = question.correctAnswer;

        // Find indices
        const userAnswerIndex = question.options.indexOf(userAnswer || "");
        const correctAnswerIndex = question.options.indexOf(correctAnswer);

        userAnswers.push(userAnswerIndex);
        correctAnswerIndices.push(correctAnswerIndex);

        if (userAnswer === correctAnswer) {
          correctAnswers++;
        }
      });

      const finalScore = Math.round((correctAnswers / questions.length) * 100);
      setScore(finalScore);

      // Save test result to Firebase
      const testResultRef = await addDoc(collection(db, "skillsValidationResults"), {
        jobSeekerId: user?.id,
        jobSeekerName: profileData?.resumeData?.personalInfo?.name || user?.name,
        jobSeekerEmail: profileData?.resumeData?.personalInfo?.email || user?.email,
        score: finalScore,
        totalQuestions: questions.length,
        correctAnswers: correctAnswers,
        answers: selectedAnswers,
        userAnswerIndices: userAnswers,
        correctAnswerIndices: correctAnswerIndices,
        questions: questions,
        completedAt: Timestamp.now(),
        timeSpent: (20 * 45) - timeRemaining,
      });

      const testResultId = testResultRef.id;

      // Update user profile with test score IMMEDIATELY (regardless of pass/fail)
      const profileRef = doc(db, "userProfiles", user?.id);
      const profileUpdateData = {
        latestSkillsTestScore: finalScore,
        latestSkillsTestDate: Timestamp.now(),
        latestSkillsTestId: testResultId,
      };

      // Check for pending HR test
      const pendingTest = getPendingTest();

      // If passed (>=60%), mark 
      if (finalScore >= 60) {
        profileUpdateData.skillsVerified = true;
        profileUpdateData.verificationScore = finalScore;
        profileUpdateData.verificationDate = Timestamp.now();

        // Award 10 points ONLY if this is the first time passing (check if skillsVerified w before)
        const wasNotVerifiedBefore = !profileData?.skillsVerified;

        if (wasNotVerifiedBefore) {
          try {
            await gamificationService.addXP(user?.id, 10);
            await gamificationService.addPoints(user?.id, 10);
            toast.success("🎉 +10 points for passing the skills test!");
          } catch (error) {
            console.error("Failed to award points:", error);
          }
        }

        toast.success("✅ Profile Verified! You can now take HR tests!");

        // Update the profile
        await updateDoc(profileRef, profileUpdateData);
        console.log("User profile updated with test score:", finalScore);

        // If there's a pending HR test, redirect directly to it
        if (pendingTest) {
          setLoading(false);
          clearPendingTest();
          toast.success(`Redirecting you to the ${pendingTest.testTitle || 'HR'} test...`);
          navigate(`/job-seeker/take-test/${pendingTest.testId}`, { replace: true });
          return;
        }
      } else {
        // If failed, update the failed score but don't mark 
        profileUpdateData.skillsVerified = false;
        toast.error("Skills not verified. You need 60% to pass.");

        // Update the profile
        await updateDoc(profileRef, profileUpdateData);
        console.log("User profile updated with test score:", finalScore);
      }

      // Navigate to results page with the test data
      // (This will show results and allow them to continue to profile or retry)
      setLoading(false);
      console.log("Navigating to results page...");

      // Construct testData object for navigation state
      const testData = {
        id: testResultId,
        score: finalScore,
        totalQuestions: questions.length,
        correctAnswers: correctAnswers,
        questions: questions,
        userAnswerIndices: userAnswers,
        correctAnswerIndices: correctAnswerIndices,
        answers: selectedAnswers,
        completedAt: Timestamp.now(),
        timeSpent: (20 * 45) - timeRemaining,
      };

      navigate(`/job-seeker/skills-test-results/${testResultId}`, {
        state: {
          testData: testData,
          profileData: profileData,
          // Pass pending test info for the results page to handle
          pendingTest: pendingTest,
        }
      });
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error("Failed to save test result. Please try again.");
      setLoading(false);
      setTestCompleted(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Preparing Your Skills Test
            </h2>
            <p className="text-gray-600 mb-2">{loadingMessage}</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              <span>This may take a few moments...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (testCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full mx-4">
          <div className="text-center">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${score >= 60 ? 'bg-green-100' : 'bg-red-100'
              }`}>
              {score >= 60 ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : (
                <Star className="w-12 h-12 text-red-600" />
              )}
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Test Completed!
            </h2>

            <div className="mb-6">
              <div className="text-6xl font-bold mb-2" style={{ color: score >= 60 ? '#059669' : '#DC2626' }}>
                {score}%
              </div>
              <p className="text-gray-600">
                You answered {questions.filter((_, index) => selectedAnswers[index] === questions[index].correctAnswer).length} out of {questions.length} questions correctly
              </p>
            </div>

            {score >= 60 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-900">Skills Verified!</span>
                </div>
                <p className="text-green-800 text-sm">
                  Congratulations! Your profile h verified with a green checkmark.
                  This verification will be visible across the platform.
                </p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Star className="w-5 h-5 text-yellow-600" />
                  <span className="font-semibold text-yellow-900">Keep Practicing!</span>
                </div>
                <p className="text-yellow-800 text-sm">
                  You need at least 60% to get verified. Don't worry, you can retake the test anytime to improve your score.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Tooltip content="Return to your profile page">
                <button
                  onClick={() => navigate("/job-seeker/profile")}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors relative"
                >
                  Back to Profile
                </button>
              </Tooltip>
              {score < 60 && (
                <Tooltip content="Retake the skills validation test">
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors relative"
                  >
                    Retake Test
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full mx-4">
          <div className="text-center">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Star className="w-12 h-12 text-red-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Skills Validation Test
            </h1>

            <p className="text-gray-600 mb-8 leading-relaxed">
              We've generated {questions.length} personalized questions based on your resume and skills.
              This test will validate your expertise and potentially verify your profile with a green checkmark.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-blue-900 mb-4">Test Instructions:</h3>
              <ul className="text-left text-blue-800 space-y-2">
                <li className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>15 minutes time limit (45 seconds per question)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Star className="w-4 h-4" />
                  <span>{questions.length} multiple choice questions</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Score 60% or higher to get verified</span>
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-5 h-5 text-yellow-600" />
                <span className="font-semibold text-yellow-900">Proctored Test</span>
              </div>
              <p className="text-yellow-800 text-sm">
                This test is monitored to ensure fair assessment. Tab switching, copy/paste, and other violations will be tracked.
              </p>
            </div>

            <Tooltip content="Begin the skills validation test">
              <button
                onClick={handleStartTest}
                className="px-8 py-4 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 transition-colors relative"
              >
                Start Test
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Proctoring Setup Modal */}
        <ProctoringSetupModal
          isOpen={showProctoringSetup}
          onClose={() => setShowProctoringSetup(false)}
          onStartProctoring={handleProctoringStarted}
          requireCamera={false}
          requireMicrophone={false}
        />
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Proctoring Banner */}
      {proctoringInitialized && proctoringStatus.isActive && (
        <ProctoringBanner
          proctoringStatus={proctoringStatus}
          onClearViolations={clearViolations}
        />
      )}

      {/* Header with timer and progress */}
      <div className={`bg-white shadow-sm border-b ${proctoringInitialized ? 'mt-16' : ''}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Skills Validation Test</h1>
              <p className="text-sm text-gray-600">
                Question {currentQuestionIndex + 1} of {questions.length}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <span className={`font-mono text-lg ${timeRemaining < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Question content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {currentQuestion.question}
          </h2>

          {preventedActions > 0 && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-red-800 text-sm font-medium">
                  Security Warning restricted action(s) detected.
                  Copy-paste and right-click are disabled during the test.
                </span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(option)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setPreventedActions(prev => prev + 1);
                }}
                style={{ userSelect: 'none' }}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${selectedAnswers[currentQuestionIndex] === option
                  ? 'border-red-600 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <span className="font-medium text-gray-900">{option}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-end mt-8">
            {/* Previous button removed per requirements */}
            <Tooltip content={currentQuestionIndex === questions.length - 1 ? 'Submit your test answers' : 'Go to next question'}>
              <button
                onClick={handleNextQuestion}
                disabled={!selectedAnswers[currentQuestionIndex]}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
              >
                {currentQuestionIndex === questions.length - 1 ? 'Submit Test' : 'Next Question'}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsValidation;
